import { Heading, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import type { ChecklistAnalysis } from "./useMembershipChecklist";

type AnalysisSummaryProps = {
  analysis: ChecklistAnalysis;
  membershipTranslationValues: Record<string, string>;
};

export const AnalysisSummary = ({
  analysis,
  membershipTranslationValues,
}: AnalysisSummaryProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.CHECKLIST.SUMMARY",
  });
  const { members, renewal } = analysis;
  const showRenewal =
    renewal.engineEnabled &&
    renewal.enabled &&
    renewal.term !== "MANUAL" &&
    renewal.term !== "LIFETIME";

  return (
    <Flex
      flexDir="column"
      gap="200"
      p="300"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="200"
    >
      <Heading size="h6">{t("TITLE", membershipTranslationValues)}</Heading>
      <Text size="sm" color="text.mid">
        {t("TOTAL", {
          ...membershipTranslationValues,
          count: members.total,
          totalListings: members.totalListings,
        })}
      </Text>
      <Text size="sm" color="text.mid">
        {t("NON_MEMBERS", {
          ...membershipTranslationValues,
          count: members.nonMembers,
        })}
      </Text>
      {members.multipleTiers > 0 && (
        <Text size="sm" color="text.warning.mid">
          {t("MULTIPLE_TIERS", { count: members.multipleTiers })}
        </Text>
      )}

      {members.byTier.length > 0 && (
        <Flex flexDir="column" gap="50">
          {members.byTier.map(tier => {
            const statuses = tier.byStatus
              .filter(s => s.count > 0)
              .map(s => `${s.count} ${s.name}`)
              .join(", ");
            return (
              <Text key={tier.tagId} size="sm" color="text.mid">
                <Text as="span" visual="smallSemiBold" color="text.dark">
                  {tier.name}
                </Text>
                {`: ${t("TIER_COUNT", { count: tier.count })}`}
                {statuses ? ` (${statuses})` : ""}
              </Text>
            );
          })}
        </Flex>
      )}

      {members.tieredWithoutStatusTag > 0 && (
        <Text size="sm" color="text.mid">
          {t("NO_STATUS", { count: members.tieredWithoutStatusTag })}
        </Text>
      )}

      {showRenewal && (
        <Flex flexDir="column" gap="50">
          <Text size="sm" color="text.mid">
            {t("WITH_DATE", { count: renewal.withRenewalDate })}
          </Text>
          <Text size="sm" color="text.mid">
            {t("WITHOUT_DATE", { count: renewal.withoutRenewalDate })}
          </Text>
          <Text size="sm" color="text.mid">
            {t("PHASES", {
              paidUp: renewal.paidUp,
              renewalWindow: renewal.renewalWindow,
              expired: renewal.expired,
            })}
          </Text>
          {renewal.tagDrift > 0 && (
            <Text size="sm" color="text.warning.mid">
              {t("TAG_DRIFT", { count: renewal.tagDrift })}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
};
